<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import SiteVisitForm from '../calendar/SiteVisitForm.vue';
import ShareDocumentsModal from './ShareDocumentsModal.vue';
import siteVisitsAPI from 'dashboard/api/nexceo/siteVisits';
import leadsAPI from 'dashboard/api/nexceo/leads';
import aiRuntimeAPI from 'dashboard/api/nexceo/aiRuntime';

const props = defineProps({
  lead: { type: Object, default: null },
  stages: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'updated']);

const store = useStore();
const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

// Header avatar — same "first letters of each word" convention used
// elsewhere in this app for contact/agent avatars; purely presentational,
// derived from the existing lead.name.
const leadInitials = computed(() => {
  const name = props.lead?.name?.trim();
  if (!name) return '?';
  const parts = name.split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();
});

// Reuses the exact same existing edit-lead route every other lead list in
// the app already links to (see VerifiedLeadsPage.vue/AllLeadsPage.vue) —
// no new route, no new permission.
function editLead() {
  if (!props.lead?.id) return;
  router.push({
    name: 'nexceo_leads_edit',
    params: { accountId: accountId.value, id: props.lead.id },
  });
}

const activities = computed(() =>
  store.getters['nexceoLeads/getActivitiesForLead'](props.lead?.id)
);
const aiLogs = computed(() =>
  store.getters['nexceoAiLogs/getLogsForLead'](props.lead?.id)
);
const pendingActions = computed(() =>
  store.getters['nexceoAiPendingActions/getActionsForLead'](props.lead?.id)
);

const isLoadingActivities = ref(false);
const isLoadingAiLogs = ref(false);
const isLoadingPendingActions = ref(false);
const showAiLogDetail = ref(null);
const rejectingActionId = ref(null);
const rejectReason = ref('');
const isMovingStage = ref(false);
const isAssigning = ref(false);
const isTogglingAi = ref(false);
const selectedStage = ref('');
const selectedAgent = ref('');
const lostReason = ref('');
const showLostConfirm = ref(false);
const showBookedConfirm = ref(false);
const showRejectConfirm = ref(false);
const rejectLeadReason = ref('');
const isVisitFormOpen = ref(false);
const visitFormPrefillType = ref('site_visit');
const isResendingConfirmation = ref(false);
const isShareDocumentsOpen = ref(false); // M26.18 (Goal 4)
const isTogglingFollowUp = ref(false);
const actionError = ref('');

// ── Chat / Requirement Summary ───────────────────────────────────────────────
const summary = ref(null);
const summaryLoading = ref(false);
let summaryPollInterval = null;

async function fetchSummary() {
  if (!props.lead?.id) return;
  summaryLoading.value = true;
  try {
    const res = await leadsAPI.getConversationSummary(props.lead.id);
    summary.value = res.data;
  } catch {
    // Non-critical — keep showing last known summary
  } finally {
    summaryLoading.value = false;
  }
}

function startSummaryPolling() {
  fetchSummary();
  summaryPollInterval = setInterval(fetchSummary, 30_000);
}

function stopSummaryPolling() {
  clearInterval(summaryPollInterval);
  summaryPollInterval = null;
}

watch(
  () => props.isOpen,
  open => {
    if (open) {
      summary.value = null;
      startSummaryPolling();
    } else {
      stopSummaryPolling();
    }
  }
);

// Reset and refetch when a different lead is opened
watch(
  () => props.lead?.id,
  () => {
    if (props.isOpen) {
      summary.value = null;
      stopSummaryPolling();
      startSummaryPolling();
    }
  }
);

onUnmounted(stopSummaryPolling);
// ────────────────────────────────────────────────────────────────────────────

function openVisitForm(type = 'site_visit') {
  visitFormPrefillType.value = type;
  isVisitFormOpen.value = true;
}

async function resendConfirmation(visitId) {
  if (!visitId || isResendingConfirmation.value) return;
  isResendingConfirmation.value = true;
  try {
    await siteVisitsAPI.resendConfirmation(visitId);
    useAlert('Confirmation resent to customer.');
  } catch {
    useAlert('Failed to resend confirmation.');
  } finally {
    isResendingConfirmation.value = false;
  }
}

const aiState = computed(() => props.lead?.ai_state?.state || null);
const aiDisabledReason = computed(
  () => props.lead?.ai_state?.disabled_reason || null
);
const aiPaused = computed(
  () => aiState.value === 'paused' || aiState.value === 'human_handoff'
);
const aiDisabledByConfig = computed(() => aiDisabledReason.value !== null);
const aiEmployee = computed(() => props.lead?.ai_employee || null);

const AI_STATUS_LABEL = {
  active: 'AI Active',
  waiting_for_customer: 'Waiting for Customer',
  paused: 'AI Paused',
  human_handoff: 'Human Takeover',
  closed: 'AI Closed',
};

const AI_DISABLED_LABEL = {
  env_disabled: 'Disabled (set NEXCEO_AI_ENABLED=true)',
  global_paused: 'AI Globally Paused',
  project_disabled: 'Disabled by Project',
  employee_inactive: 'AI Employee Inactive',
  auto_reply_off: 'Auto-Reply Off',
  openai_missing: 'OpenAI Key Missing',
};

const aiStatusLabel = computed(() => {
  if (aiDisabledByConfig.value) {
    return AI_DISABLED_LABEL[aiDisabledReason.value] || 'AI Disabled';
  }
  if (!aiState.value) {
    return aiEmployee.value ? 'AI Active' : 'No AI Assigned';
  }
  return AI_STATUS_LABEL[aiState.value] || aiState.value || 'Unknown';
});

const aiStatusClass = computed(() => {
  if (aiDisabledByConfig.value)
    return 'bg-n-alpha-2 text-n-slate-8 border-n-weak';
  const state = aiState.value;
  if (!state) {
    return aiEmployee.value
      ? 'bg-success-50 text-success-800 border-success-200 dark:bg-success-800/20 dark:text-success-400 dark:border-success-500/30'
      : 'bg-n-alpha-2 text-n-slate-8 border-n-weak';
  }
  switch (state) {
    case 'active':
    case 'waiting_for_customer':
      return 'bg-success-50 text-success-800 border-success-200 dark:bg-success-800/20 dark:text-success-400 dark:border-success-500/30';
    case 'paused':
    case 'human_handoff':
      return 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-800/20 dark:text-amber-400 dark:border-amber-500/30';
    case 'closed':
      return 'bg-n-alpha-2 text-n-slate-8 border-n-weak';
    default:
      return 'bg-n-alpha-2 text-n-slate-8 border-n-weak';
  }
});

const aiStatusIcon = computed(() => {
  if (aiDisabledByConfig.value) return 'i-lucide-ban';
  const state = aiState.value;
  if (!state) return aiEmployee.value ? 'i-lucide-bot' : 'i-lucide-help-circle';
  switch (state) {
    case 'active':
      return 'i-lucide-bot';
    case 'waiting_for_customer':
      return 'i-lucide-clock';
    case 'paused':
      return 'i-lucide-pause-circle';
    case 'human_handoff':
      return 'i-lucide-user-check';
    case 'closed':
      return 'i-lucide-x-circle';
    default:
      return 'i-lucide-help-circle';
  }
});

async function onPauseAi() {
  if (!props.lead) return;
  isTogglingAi.value = true;
  actionError.value = '';
  try {
    await store.dispatch('nexceoLeads/pauseAi', props.lead.id);
    emit('updated');
  } catch {
    actionError.value = 'Failed to pause AI';
  } finally {
    isTogglingAi.value = false;
  }
}

async function onResumeAi() {
  if (!props.lead) return;
  isTogglingAi.value = true;
  actionError.value = '';
  try {
    await store.dispatch('nexceoLeads/resumeAi', props.lead.id);
    emit('updated');
  } catch {
    actionError.value = 'Failed to resume AI';
  } finally {
    isTogglingAi.value = false;
  }
}

async function onPauseFollowUps() {
  if (!props.lead) return;
  isTogglingFollowUp.value = true;
  actionError.value = '';
  try {
    await store.dispatch('nexceoLeads/pauseFollowUps', props.lead.id);
    emit('updated');
  } catch {
    actionError.value = 'Failed to pause follow-ups';
  } finally {
    isTogglingFollowUp.value = false;
  }
}

async function onResumeFollowUps() {
  if (!props.lead) return;
  isTogglingFollowUp.value = true;
  actionError.value = '';
  try {
    await store.dispatch('nexceoLeads/resumeFollowUps', props.lead.id);
    emit('updated');
  } catch {
    actionError.value = 'Failed to resume follow-ups';
  } finally {
    isTogglingFollowUp.value = false;
  }
}

watch(
  () => props.lead,
  async lead => {
    if (!lead) return;
    selectedStage.value = lead.kanban_stage?.key || '';
    selectedAgent.value = lead.assigned_agent_id || '';
    lostReason.value = '';
    showLostConfirm.value = false;
    showBookedConfirm.value = false;
    showRejectConfirm.value = false;
    rejectLeadReason.value = '';
    actionError.value = '';
    isLoadingActivities.value = true;
    isLoadingAiLogs.value = true;
    isLoadingPendingActions.value = true;
    try {
      const accountId = lead.account_id;
      await Promise.all([
        store.dispatch('nexceoLeads/fetchActivities', lead.id),
        store.dispatch('nexceoAiLogs/fetchLeadLogs', {
          accountId,
          leadId: lead.id,
        }),
        store.dispatch('nexceoAiPendingActions/fetchForLead', {
          accountId,
          leadId: lead.id,
        }),
      ]);
    } finally {
      isLoadingActivities.value = false;
      isLoadingAiLogs.value = false;
      isLoadingPendingActions.value = false;
    }
  },
  { immediate: true }
);

const budgetDisplay = computed(() => {
  const lead = props.lead;
  if (!lead || (!lead.budget_min && !lead.budget_max)) return '—';
  const sym = lead.currency === 'INR' ? '₹' : lead.currency || '';
  const fmt = v => {
    if (v >= 10000000) return `${(v / 10000000).toFixed(2)}Cr`;
    if (v >= 100000) return `${(v / 100000).toFixed(2)}L`;
    return Number(v).toLocaleString();
  };
  if (lead.budget_min && lead.budget_max) {
    return `${sym}${fmt(lead.budget_min)} – ${sym}${fmt(lead.budget_max)}`;
  }
  return `${sym}${fmt(lead.budget_min || lead.budget_max)}`;
});

const otherStages = computed(() =>
  props.stages.filter(s => s.key !== props.lead?.kanban_stage?.key)
);

async function onMoveStage() {
  if (!selectedStage.value || !props.lead) return;
  isMovingStage.value = true;
  actionError.value = '';
  try {
    await store.dispatch('nexceoLeads/moveStage', {
      leadId: props.lead.id,
      stageKey: selectedStage.value,
    });
    emit('updated');
  } catch (e) {
    actionError.value = t('NEXCEO.KANBAN.MOVE_STAGE_ERROR');
  } finally {
    isMovingStage.value = false;
  }
}

async function onAssignAgent() {
  if (!props.lead) return;
  isAssigning.value = true;
  actionError.value = '';
  try {
    await store.dispatch('nexceoLeads/assignAgent', {
      leadId: props.lead.id,
      agentId: selectedAgent.value || null,
    });
    emit('updated');
  } catch {
    actionError.value = t('NEXCEO.LEAD.ERROR');
  } finally {
    isAssigning.value = false;
  }
}

async function onMarkLost() {
  actionError.value = '';
  try {
    await store.dispatch('nexceoLeads/markLost', {
      leadId: props.lead.id,
      reason: lostReason.value,
    });
    showLostConfirm.value = false;
    emit('updated');
  } catch {
    actionError.value = t('NEXCEO.LEAD.ERROR');
  }
}

async function onMarkBooked() {
  actionError.value = '';
  try {
    await store.dispatch('nexceoLeads/markBooked', props.lead.id);
    showBookedConfirm.value = false;
    emit('updated');
  } catch (e) {
    const msg = e?.response?.data?.error;
    actionError.value = msg || t('NEXCEO.LEAD.ERROR');
  }
}

const isVerifying = ref(false);
const isRejecting = ref(false);
const isRestoring = ref(false);

const REJECT_REASONS = [
  'Wrong number / unreachable',
  'Spam / not genuine',
  'Not interested',
  'Duplicate lead',
  'Budget mismatch',
  'Out of scope / unrelated',
  'Other',
];

const isRejectedOrJunk = computed(() => props.lead?.rejected_or_junk === true);

async function onVerify() {
  if (!props.lead) return;
  isVerifying.value = true;
  actionError.value = '';
  try {
    await import('dashboard/api/nexceo/leads').then(m =>
      m.default.verify(props.lead.id)
    );
    emit('updated');
  } catch {
    actionError.value = 'Failed to verify lead.';
  } finally {
    isVerifying.value = false;
  }
}

async function onConfirmRejectLead() {
  if (!props.lead) return;
  isRejecting.value = true;
  actionError.value = '';
  try {
    await import('dashboard/api/nexceo/leads').then(m =>
      m.default.reject(props.lead.id, rejectLeadReason.value)
    );
    showRejectConfirm.value = false;
    rejectLeadReason.value = '';
    emit('updated');
  } catch {
    actionError.value = 'Failed to reject lead.';
  } finally {
    isRejecting.value = false;
  }
}

async function onRestoreLead() {
  if (!props.lead) return;
  isRestoring.value = true;
  actionError.value = '';
  try {
    await import('dashboard/api/nexceo/leads').then(m =>
      m.default.restoreLead(props.lead.id)
    );
    emit('updated');
  } catch {
    actionError.value = 'Failed to restore lead.';
  } finally {
    isRestoring.value = false;
  }
}

const isSendingAlert = ref(false);
async function onSendTeamAlert(force = false) {
  if (!props.lead) return;
  isSendingAlert.value = true;
  actionError.value = '';
  try {
    await import('dashboard/api/nexceo/leads').then(m =>
      m.default.sendTeamAlert(props.lead.id, { force })
    );
    actionError.value = '';
    // Brief success indicator
    isSendingAlert.value = false;
    window.bus?.$emit('newToastMessage', {
      message: 'Team alert sent.',
      action: null,
    });
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      'Failed to send team alert.';
    if (!force && msg.includes('already sent')) {
      if (window.confirm('Alert already sent. Resend to all team members?')) {
        await onSendTeamAlert(true);
        return;
      }
    } else {
      actionError.value = msg;
    }
  } finally {
    isSendingAlert.value = false;
  }
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatKey(key) {
  return (key || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function activityIcon(type) {
  const map = {
    stage_changed: 'i-lucide-arrow-right-circle',
    agent_assigned: 'i-lucide-user',
    created: 'i-lucide-plus-circle',
    marked_lost: 'i-lucide-x-circle',
    marked_booked: 'i-lucide-check-circle',
    field_updated: 'i-lucide-pencil',
    notification_sent: 'i-lucide-bell',
    workforce_event: 'i-lucide-shuffle',
    human_handoff: 'i-lucide-user-check',
  };
  return map[type] || 'i-lucide-info';
}

// M26.12 Part 14 — Lead Timeline. Distinguishes AI/human/system actions at
// a glance, using the actor_category the backend already resolved
// (Nexceo::LeadActivityPresenter) rather than re-deriving it here.
const ACTOR_CATEGORY_STYLE = {
  ai: { icon: 'i-lucide-bot', class: 'text-purple-600 dark:text-purple-400' },
  human: {
    icon: 'i-lucide-user-round',
    class: 'text-green-600 dark:text-green-400',
  },
  system: { icon: 'i-lucide-cog', class: 'text-n-slate-7' },
};

function actorStyle(category) {
  return ACTOR_CATEGORY_STYLE[category] || ACTOR_CATEGORY_STYLE.system;
}

// ── Ownership journey (V1 Admin Bridge Part 9) ───────────────────────────
// Builds "ZOYA → Sales Manager → Documentation AI → ... → Human" from the
// SAME activity log rendered below (Nexceo::Ai::WorkforceEventBus already
// logs ownership/capability transfers as regular Nexceo::LeadActivity rows
// with activity_type 'workforce_event') — no extra query, no new storage.
const ROLE_LABELS = {
  company_ai: 'ZOYA',
  project_ai: 'Project AI',
  human_executive: 'Human',
  project_manager: 'Project Manager',
  shared: 'Shared',
  sales_manager: 'Sales Manager AI',
  documentation: 'Documentation AI',
  calendar: 'Calendar AI',
  marketing: 'Marketing AI',
  finance: 'Finance AI',
  legal: 'Legal AI',
  support: 'Support AI',
  nexa: 'NEXA',
  crm: 'CRM AI',
  project: 'Project AI',
  company_coordinator: 'ZOYA',
};

function roleLabel(key) {
  return ROLE_LABELS[key] || formatKey(key);
}

const ownershipChain = computed(() => {
  const relevant = (activities.value || [])
    .filter(
      a =>
        a.activity_type === 'workforce_event' ||
        a.activity_type === 'human_handoff'
    )
    .slice()
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const chain = [];
  relevant.forEach(a => {
    if (a.activity_type === 'human_handoff') {
      chain.push({ label: 'Human', at: a.created_at });
      return;
    }
    const { event_type: eventType, from, to } = a.data || {};
    if (!['ownership_transfer', 'capability_transfer'].includes(eventType))
      return;
    if (!chain.length && from)
      chain.push({ label: roleLabel(from), at: a.created_at });
    if (to) chain.push({ label: roleLabel(to), at: a.created_at });
  });

  return chain.filter(
    (node, idx) => idx === 0 || node.label !== chain[idx - 1].label
  );
});

function aiLogStatusClass(status) {
  return (
    {
      success:
        'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300',
      failed: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
      skipped: 'bg-n-alpha-2 text-n-slate-9',
      warning:
        'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
    }[status] || 'bg-n-alpha-2 text-n-slate-9'
  );
}

function aiLogEventIcon(event) {
  const map = {
    provider_called: 'i-lucide-zap',
    response_generated: 'i-lucide-message-square',
    tool_action_executed: 'i-lucide-wrench',
    tool_action_skipped: 'i-lucide-ban',
    handoff_triggered: 'i-lucide-user-check',
    customer_reply_sent: 'i-lucide-send',
    error: 'i-lucide-alert-circle',
    ai_paused: 'i-lucide-pause-circle',
    extraction_completed: 'i-lucide-scan-text',
    scoring_completed: 'i-lucide-bar-chart',
  };
  return map[event] || 'i-lucide-activity';
}

async function approvePendingAction(action) {
  actionError.value = '';
  try {
    await store.dispatch('nexceoAiPendingActions/approve', {
      accountId: props.lead.account_id,
      actionId: action.id,
    });
    await store.dispatch('nexceoAiPendingActions/fetchForLead', {
      accountId: props.lead.account_id,
      leadId: props.lead.id,
    });
    emit('updated');
  } catch (e) {
    actionError.value = e?.response?.data?.error || 'Approval failed.';
  }
}

function startRejectAction(action) {
  rejectingActionId.value = action.id;
  rejectReason.value = '';
}

async function submitRejectAction() {
  if (!rejectingActionId.value) return;
  actionError.value = '';
  try {
    await store.dispatch('nexceoAiPendingActions/reject', {
      accountId: props.lead.account_id,
      actionId: rejectingActionId.value,
      reason: rejectReason.value,
    });
    rejectingActionId.value = null;
    rejectReason.value = '';
    await store.dispatch('nexceoAiPendingActions/fetchForLead', {
      accountId: props.lead.account_id,
      leadId: props.lead.id,
    });
    emit('updated');
  } catch (e) {
    actionError.value = e?.response?.data?.error || 'Rejection failed.';
  }
}

const PENDING_ACTION_LABELS = {
  share_document_requires_approval: 'Document (Approval Required)',
  share_price_sheet: 'Price Sheet',
  share_payment_plan: 'Payment Plan',
  share_legal: 'Legal Document',
  share_rera: 'RERA Document',
  share_inventory: 'Inventory',
  send_offer_message: 'Offer Message',
};

function pendingActionLabel(type) {
  return PENDING_ACTION_LABELS[type] || type?.replace(/_/g, ' ');
}

const CHANNEL_LABELS = {
  whatsapp: 'WhatsApp',
  facebook: 'Facebook',
  instagram: 'Instagram',
  web_widget: 'Website',
  email: 'Email',
  api: 'API',
  manual: 'Manual Entry',
  website_contact_form: 'Website Contact Form',
  website_popup: 'Website Popup',
};

function humanizeChannel(ch) {
  return CHANNEL_LABELS[ch] || (ch ? ch.replace(/_/g, ' ') : '—');
}

const pendingCount = computed(
  () => (pendingActions.value || []).filter(a => a.status === 'pending').length
);

// ── AI Intelligence tab (V1 Admin Bridge Part 3/8) ───────────────────────────
// Read-only snapshot from Nexceo::Ai::RuntimeInspector — lazy loaded only
// when the tab is opened (Part 15: no work done for a tab nobody views).
const activeTab = ref('details');
const runtimeSnapshot = ref(null);
const runtimeLoading = ref(false);
const runtimeError = ref('');

async function fetchRuntimeSnapshot() {
  if (!props.lead?.id) return;
  runtimeLoading.value = true;
  runtimeError.value = '';
  try {
    const { data } = await aiRuntimeAPI.show(props.lead.id);
    if (data?.error) {
      runtimeError.value = data.error;
      runtimeSnapshot.value = null;
    } else {
      runtimeSnapshot.value = data;
    }
  } catch (e) {
    runtimeError.value =
      e?.response?.data?.error ??
      'Failed to load AI intelligence for this lead.';
  } finally {
    runtimeLoading.value = false;
  }
}

function selectTab(tab) {
  activeTab.value = tab;
  if (tab === 'ai_intelligence' && !runtimeSnapshot.value) {
    fetchRuntimeSnapshot();
  }
}

watch(
  () => props.lead?.id,
  () => {
    activeTab.value = 'details';
    runtimeSnapshot.value = null;
    runtimeError.value = '';
  }
);

function objectEntries(obj) {
  return Object.entries(obj || {}).filter(([, v]) => v !== null && v !== '');
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <!-- Backdrop -->
  <Teleport to="body">
    <transition name="drawer">
      <div
        v-if="isOpen && lead"
        class="fixed inset-0 z-[9998] flex justify-end"
      >
        <!-- overlay -->
        <div
          class="absolute inset-0 bg-n-slate-12/30 dark:bg-n-slate-1/50"
          @click="$emit('close')"
        />
        <!-- Drawer panel -->
        <div
          class="relative z-10 flex flex-col w-full max-w-lg h-full bg-white dark:bg-n-solid-2 shadow-xl overflow-hidden"
        >
          <!-- Header -->
          <div
            class="flex flex-col gap-3 px-5 py-4 border-b border-n-weak dark:border-n-strong shrink-0"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="flex items-start gap-3 min-w-0">
                <span
                  class="flex items-center justify-center size-11 rounded-full bg-woot-50 dark:bg-woot-500/10 text-woot-600 dark:text-woot-400 text-sm font-bold shrink-0"
                >
                  {{ leadInitials }}
                </span>
                <div class="min-w-0 pt-0.5">
                  <div class="flex items-center gap-2 flex-wrap">
                    <h2 class="font-bold text-base text-n-slate-12 truncate">
                      {{ lead.name || '(Unnamed Lead)' }}
                    </h2>
                    <span
                      v-if="lead.priority && lead.priority !== 'normal'"
                      class="text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize shrink-0"
                      :class="{
                        'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400':
                          lead.priority === 'urgent',
                        'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400':
                          lead.priority === 'high',
                        'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400':
                          lead.priority === 'low',
                      }"
                    >
                      {{ lead.priority }}
                    </span>
                    <span
                      v-if="lead.verification_status === 'human_verified'"
                      class="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 shrink-0"
                    >
                      <span class="i-lucide-shield-check size-3" />
                      Human Verified
                    </span>
                    <span
                      v-else-if="lead.verification_status === 'ai_verified'"
                      class="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-woot-50 text-woot-700 dark:bg-woot-900/20 dark:text-woot-400 shrink-0"
                    >
                      <span class="i-lucide-sparkles size-3" />
                      AI Verified
                    </span>
                  </div>
                  <p class="text-xs text-n-slate-9 mt-0.5 truncate">
                    {{ lead.project?.name || '—' }}
                    <span v-if="lead.kanban_stage">
                      · {{ lead.kanban_stage.name }}
                    </span>
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <div
                  v-if="lead.lead_score !== null && lead.lead_score !== undefined"
                  class="text-right"
                >
                  <p class="text-[10px] text-n-slate-7 leading-none">
                    Lead Score
                  </p>
                  <p class="text-sm font-bold text-n-slate-12 leading-tight">
                    {{ lead.lead_score }}<span class="text-n-slate-7">/100</span>
                  </p>
                </div>
                <button
                  class="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg !border !border-solid border-n-weak text-n-slate-10 hover:bg-n-alpha-2 transition-colors"
                  @click="editLead"
                >
                  <span class="i-lucide-pencil size-3.5" />
                  Edit
                </button>
                <button
                  class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-8 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors shrink-0"
                  @click="$emit('close')"
                >
                  <span class="i-lucide-x size-5" />
                </button>
              </div>
            </div>

            <!-- Contact strip -->
            <div
              class="flex items-center gap-4 text-xs text-n-slate-9 flex-wrap"
            >
              <span class="flex items-center gap-1.5">
                <span class="i-lucide-phone size-3.5 text-n-slate-7" />
                {{ lead.phone_number || '—' }}
              </span>
              <span class="flex items-center gap-1.5 truncate">
                <span class="i-lucide-mail size-3.5 text-n-slate-7 shrink-0" />
                {{ lead.email || '—' }}
              </span>
              <span class="flex items-center gap-1.5">
                <span class="i-lucide-radio-tower size-3.5 text-n-slate-7" />
                {{ humanizeChannel(lead.source_channel) }}
              </span>
            </div>
          </div>

          <!-- Tab bar -->
          <div
            class="flex items-center gap-0 px-3 border-b border-n-weak dark:border-n-strong shrink-0"
          >
            <button
              class="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium !border-b-2 !border-solid transition-colors"
              :class="
                activeTab === 'details'
                  ? '!border-woot-500 text-woot-600 dark:text-woot-400'
                  : '!border-transparent text-n-slate-9 hover:text-n-slate-12'
              "
              @click="selectTab('details')"
            >
              <span class="i-lucide-layout-dashboard size-3.5" />
              Overview
            </button>
            <button
              class="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium !border-b-2 !border-solid transition-colors"
              :class="
                activeTab === 'ai_intelligence'
                  ? '!border-woot-500 text-woot-600 dark:text-woot-400'
                  : '!border-transparent text-n-slate-9 hover:text-n-slate-12'
              "
              @click="selectTab('ai_intelligence')"
            >
              <span class="i-lucide-brain-circuit size-3.5" />
              AI Intelligence
            </button>
          </div>

          <!-- Scrollable body -->
          <div
            v-if="activeTab === 'details'"
            class="flex-1 overflow-y-auto p-4 flex flex-col gap-4"
          >
            <!-- Action error -->
            <div
              v-if="actionError"
              class="text-xs text-ruby-700 bg-ruby-50 dark:bg-ruby-500/10 border border-ruby-200 rounded-xl px-4 py-3"
            >
              {{ actionError }}
            </div>

            <!-- Lead Overview -->
            <section class="rounded-xl border border-n-weak bg-n-background p-4">
              <h3 class="flex items-center gap-2 text-sm font-bold text-n-slate-12 mb-3">
                <span
                  class="flex items-center justify-center size-6 rounded-md bg-woot-50 dark:bg-woot-500/10 text-woot-600 dark:text-woot-400 shrink-0"
                >
                  <span class="i-lucide-user-round size-3.5" />
                </span>
                Lead Overview
              </h3>
              <dl
                class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm"
              >
                <div>
                  <dt class="text-n-slate-7 text-xs">Project</dt>
                  <dd class="text-n-slate-12 font-medium truncate">
                    {{ lead.project?.name || '—' }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-7 text-xs">
                    {{ t('NEXCEO.LEAD.FIELDS.SOURCE_CHANNEL') }}
                  </dt>
                  <dd class="text-n-slate-12">
                    {{ humanizeChannel(lead.source_channel) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-7 text-xs">
                    {{ t('NEXCEO.LEAD.FIELDS.PRIORITY') }}
                  </dt>
                  <dd>
                    <span
                      class="text-xs px-1.5 py-0.5 rounded-full font-medium border"
                      :class="{
                        'bg-n-alpha-2 text-n-slate-8 border-n-weak':
                          !lead.priority || lead.priority === 'normal',
                        'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400':
                          lead.priority === 'high',
                        'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400':
                          lead.priority === 'urgent',
                        'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400':
                          lead.priority === 'low',
                      }"
                    >
                      {{ lead.priority || 'normal' }}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-7 text-xs">
                    {{ t('NEXCEO.LEAD.FIELDS.STATUS') }}
                  </dt>
                  <dd class="text-n-slate-12 capitalize font-medium">
                    {{ lead.qualification_status || '—' }}
                  </dd>
                </div>
                <div v-if="lead.lead_type">
                  <dt class="text-n-slate-7 text-xs">Lead Type</dt>
                  <dd>
                    <span
                      class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border"
                      :class="
                        lead.lead_type === 'broker'
                          ? 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-500/30'
                          : 'bg-woot-50 text-woot-700 border-woot-200 dark:bg-woot-900/20 dark:text-woot-400 dark:border-woot-500/30'
                      "
                    >
                      <span
                        :class="
                          lead.lead_type === 'broker'
                            ? 'i-lucide-briefcase'
                            : 'i-lucide-user'
                        "
                        class="size-3"
                      />
                      {{ lead.lead_type }}
                    </span>
                    <span
                      v-if="lead.broker_detected"
                      class="ml-1 text-xs text-violet-600 dark:text-violet-400"
                      >· auto-detected</span
                    >
                  </dd>
                </div>
                <div v-if="lead.preferred_language">
                  <dt class="text-n-slate-7 text-xs">Language</dt>
                  <dd>
                    <span
                      class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-9 border border-n-weak"
                    >
                      <span class="i-lucide-languages size-3" />
                      {{ lead.preferred_language }}
                    </span>
                  </dd>
                </div>
                <div v-if="lead.created_at">
                  <dt class="text-n-slate-7 text-xs">Created On</dt>
                  <dd class="text-n-slate-12">
                    {{
                      new Date(lead.created_at).toLocaleString([], {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    }}
                  </dd>
                </div>
              </dl>
            </section>

            <!-- Budget & Property -->
            <section class="rounded-xl border border-n-weak bg-n-background p-4">
              <h3 class="flex items-center gap-2 text-sm font-bold text-n-slate-12 mb-3">
                <span
                  class="flex items-center justify-center size-6 rounded-md bg-yellow-50 dark:bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 shrink-0"
                >
                  <span class="i-lucide-wallet size-3.5" />
                </span>
                Budget &amp; Property
              </h3>
              <dl
                class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm"
              >
                <div>
                  <dt class="text-n-slate-8 text-xs">
                    {{ t('NEXCEO.LEAD.FIELDS.BUDGET') }}
                  </dt>
                  <dd class="text-n-slate-12 font-medium">
                    {{ budgetDisplay }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-8 text-xs">
                    {{ t('NEXCEO.LEAD.FIELDS.BEDROOMS') }}
                  </dt>
                  <dd class="text-n-slate-12">{{ lead.bedrooms || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-n-slate-8 text-xs">
                    {{ t('NEXCEO.LEAD.FIELDS.PREFERRED_LOCATION') }}
                  </dt>
                  <dd class="text-n-slate-12">
                    {{ lead.preferred_location || '—' }}
                    <span
                      v-if="
                        lead.preferred_location &&
                        lead.preferred_location_source === 'project'
                      "
                      class="ml-1 text-xxs text-n-slate-7 italic"
                      >(from project)</span
                    >
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-8 text-xs">
                    {{ t('NEXCEO.LEAD.FIELDS.PROPERTY_TYPE') }}
                  </dt>
                  <dd class="text-n-slate-12 capitalize">
                    {{ lead.property_type || '—' }}
                    <span
                      v-if="
                        lead.property_type &&
                        lead.property_type_source === 'project'
                      "
                      class="ml-1 text-xxs text-n-slate-7 italic"
                      >(from project)</span
                    >
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-8 text-xs">
                    {{ t('NEXCEO.LEAD.FIELDS.PURPOSE') }}
                  </dt>
                  <dd class="text-n-slate-12 capitalize">
                    {{ lead.purpose?.replace('_', ' ') || '—' }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-8 text-xs">
                    {{ t('NEXCEO.LEAD.FIELDS.TIMELINE') }}
                  </dt>
                  <dd class="text-n-slate-12 capitalize">
                    {{ lead.timeline?.replace('_', ' ') || '—' }}
                  </dd>
                </div>
              </dl>
            </section>

            <!-- AI Intelligence -->
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <section
              v-if="
                lead.ai_summary ||
                lead.preference_summary ||
                lead.objection_summary ||
                lead.score_breakdown
              "
              class="px-5 py-4 space-y-3"
            >
              <h3
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest flex items-center gap-1.5"
              >
                <span class="i-lucide-brain size-3.5" />
                AI Intelligence
              </h3>

              <!-- Score breakdown -->
              <div
                v-if="
                  lead.score_breakdown &&
                  Object.keys(lead.score_breakdown).length
                "
              >
                <p class="text-xs text-n-slate-8 mb-1.5 font-medium">
                  Score Breakdown
                </p>
                <div class="space-y-1">
                  <div
                    v-for="(pts, factor) in lead.score_breakdown"
                    :key="factor"
                    class="flex items-center justify-between text-xs"
                  >
                    <span class="text-n-slate-9 capitalize">{{
                      String(factor).replace(/_/g, ' ')
                    }}</span>
                    <span
                      class="font-semibold tabular-nums"
                      :class="
                        pts >= 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      "
                    >
                      {{ pts >= 0 ? '+' : '' }}{{ pts }}
                    </span>
                  </div>
                </div>
                <!-- Score bar -->
                <div
                  class="mt-2 h-1.5 rounded-full bg-n-alpha-3 overflow-hidden"
                >
                  <div
                    class="h-full rounded-full transition-all"
                    :class="{
                      'bg-green-500': (lead.lead_score ?? 0) >= 80,
                      'bg-amber-500':
                        (lead.lead_score ?? 0) >= 60 &&
                        (lead.lead_score ?? 0) < 80,
                      'bg-blue-500':
                        (lead.lead_score ?? 0) >= 30 &&
                        (lead.lead_score ?? 0) < 60,
                      'bg-n-slate-5': (lead.lead_score ?? 0) < 30,
                    }"
                    :style="{
                      width: `${Math.min(lead.lead_score ?? 0, 100)}%`,
                    }"
                  />
                </div>
              </div>

              <!-- AI summary -->
              <div v-if="lead.ai_summary">
                <p class="text-xs text-n-slate-8 mb-1 font-medium">
                  AI Summary
                </p>
                <p
                  class="text-xs text-n-slate-11 leading-relaxed bg-n-alpha-1 rounded-lg px-3 py-2 border border-n-weak"
                >
                  {{ lead.ai_summary }}
                </p>
              </div>

              <!-- Preference summary -->
              <div v-if="lead.preference_summary">
                <p class="text-xs text-n-slate-8 mb-1 font-medium">
                  Property Preference
                </p>
                <p class="text-xs text-n-slate-11 leading-relaxed">
                  {{ lead.preference_summary }}
                </p>
              </div>

              <!-- Objection summary -->
              <div v-if="lead.objection_summary">
                <div
                  class="flex items-start gap-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 rounded-lg px-3 py-2"
                >
                  <span
                    class="i-lucide-alert-triangle size-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
                  />
                  <div>
                    <p
                      class="text-xs text-amber-700 dark:text-amber-400 font-medium mb-0.5"
                    >
                      Objection / Concern
                    </p>
                    <p class="text-xs text-amber-700 dark:text-amber-300">
                      {{ lead.objection_summary }}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <!-- ── Chat / Requirement Summary ────────────────────────── -->
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <section class="rounded-xl border border-n-weak bg-n-background p-4 space-y-3">
              <div class="flex items-center justify-between">
                <h3 class="flex items-center gap-2 text-sm font-bold text-n-slate-12">
                  <span
                    class="flex items-center justify-center size-6 rounded-md bg-woot-50 dark:bg-woot-500/10 text-woot-600 dark:text-woot-400 shrink-0"
                  >
                    <span class="i-lucide-message-circle size-3.5" />
                  </span>
                  Recent Conversation
                </h3>
                <span
                  v-if="summaryLoading"
                  class="i-lucide-loader-2 size-3.5 animate-spin text-n-slate-6"
                />
                <span
                  v-else-if="summary"
                  class="text-xs text-n-slate-6"
                  :title="summary.conversation_summary_updated_at"
                >
                  Updated
                  {{
                    summary.conversation_summary_updated_at
                      ? new Date(
                          summary.conversation_summary_updated_at
                        ).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—'
                  }}
                </span>
              </div>

              <!-- Requirement summary -->
              <div
                v-if="
                  summary?.requirement_summary || summary?.requirement_notes
                "
              >
                <p class="text-xs text-n-slate-8 mb-1 font-medium">
                  Requirement Summary
                </p>
                <p
                  class="text-xs text-n-slate-11 leading-relaxed bg-n-alpha-1 rounded-lg px-3 py-2 border border-n-weak"
                >
                  {{
                    summary?.requirement_summary ||
                    summary?.requirement_notes ||
                    '—'
                  }}
                </p>
              </div>

              <!-- Latest customer intent -->
              <div v-if="summary?.latest_customer_intent">
                <p class="text-xs text-n-slate-8 mb-1 font-medium">
                  Latest Intent
                </p>
                <span
                  class="inline-flex items-center gap-1 text-xs bg-woot-50 dark:bg-woot-900/20 text-woot-700 dark:text-woot-300 border border-woot-200 dark:border-woot-500/30 rounded-full px-2 py-0.5"
                >
                  <span class="i-lucide-target size-3" />
                  {{ summary.latest_customer_intent }}
                </span>
              </div>

              <!-- Recent conversation -->
              <div
                v-if="
                  summary?.messages_preview && summary.messages_preview.length
                "
              >
                <p class="text-xs text-n-slate-8 mb-2 font-medium">
                  Recent Conversation
                </p>
                <div class="space-y-1.5">
                  <div
                    v-for="(msg, idx) in summary.messages_preview"
                    :key="idx"
                    class="flex items-start gap-2"
                  >
                    <!-- Sender badge -->
                    <span
                      class="shrink-0 mt-0.5 inline-flex items-center text-[10px] font-semibold rounded-full px-1.5 py-0.5 leading-none"
                      :class="{
                        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300':
                          msg.sender_type === 'customer',
                        'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300':
                          msg.sender_type === 'ai',
                        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300':
                          msg.sender_type === 'human',
                      }"
                    >
                      {{ msg.sender_name }}
                    </span>
                    <p class="text-xs text-n-slate-11 leading-relaxed min-w-0">
                      {{ msg.content }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Empty state -->
              <p
                v-if="!summaryLoading && !summary"
                class="text-xs text-n-slate-6 italic"
              >
                No conversation summary yet.
              </p>
            </section>

            <!-- Follow-up & site visit -->
            <section class="rounded-xl border border-n-weak bg-n-background p-4">
              <h3 class="flex items-center gap-2 text-sm font-bold text-n-slate-12 mb-3">
                <span
                  class="flex items-center justify-center size-6 rounded-md bg-woot-50 dark:bg-woot-500/10 text-woot-600 dark:text-woot-400 shrink-0"
                >
                  <span class="i-lucide-clipboard-check size-3.5" />
                </span>
                {{ t('NEXCEO.LEAD.DRAWER.QUALIFICATION') }}
              </h3>
              <dl
                class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm"
              >
                <div>
                  <dt class="text-n-slate-8 text-xs">
                    {{ t('NEXCEO.LEAD.FIELDS.NEXT_FOLLOW_UP') }}
                  </dt>
                  <dd class="text-n-slate-12">
                    {{ formatDate(lead.next_follow_up_at) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-8 text-xs">
                    {{ t('NEXCEO.LEAD.FIELDS.SITE_VISIT_STATUS') }}
                  </dt>
                  <dd class="text-n-slate-12 capitalize">
                    {{
                      t(
                        `NEXCEO.KANBAN.SITE_VISIT_STATUS.${lead.site_visit_status || 'not_scheduled'}`
                      )
                    }}
                  </dd>
                </div>
                <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
                <div v-if="lead?.next_site_visit_at">
                  <dt class="text-n-slate-8 text-xs">Next Site Visit</dt>
                  <dd
                    class="text-n-slate-12 font-medium flex items-center gap-1"
                    :class="
                      lead.next_site_visit_type === 'video_call'
                        ? 'text-woot-600'
                        : 'text-green-700'
                    "
                  >
                    <span
                      :class="
                        lead.next_site_visit_type === 'video_call'
                          ? 'i-lucide-video size-3.5'
                          : 'i-lucide-calendar-check size-3.5'
                      "
                    />
                    {{ formatDate(lead.next_site_visit_at) }}
                    <span
                      v-if="lead.next_site_visit_type"
                      class="text-xs text-n-slate-7 capitalize ml-1"
                    >
                      ({{ lead.next_site_visit_type.replace('_', ' ') }})
                    </span>
                  </dd>
                  <dd v-if="lead.next_site_visit_meeting_url" class="mt-1">
                    <a
                      :href="lead.next_site_visit_meeting_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center gap-1 text-xs text-woot-500 hover:underline"
                    >
                      <span class="i-lucide-video size-3" />
                      Join Meeting
                    </a>
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-8 text-xs">Follow-ups Sent</dt>
                  <dd class="text-n-slate-12">
                    {{ lead.follow_up_count || 0 }} / 3
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-8 text-xs">Last Followed Up</dt>
                  <dd class="text-n-slate-12">
                    {{ formatDate(lead.last_followed_up_at) }}
                  </dd>
                </div>
              </dl>

              <!-- Follow-up pause/resume controls -->
              <div class="mt-3 flex items-center gap-2 flex-wrap">
                <span
                  v-if="lead.follow_up_paused"
                  class="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium border border-amber-200"
                >
                  Follow-ups Paused
                </span>
                <button
                  v-if="!lead.follow_up_paused"
                  :disabled="isTogglingFollowUp"
                  class="text-xs px-3 py-1 rounded !border !border-solid border-n-weak text-n-slate-10 hover:text-ruby-600 hover:border-ruby-300 transition-colors disabled:opacity-50"
                  @click="onPauseFollowUps"
                >
                  Pause Follow-ups
                </button>
                <button
                  v-if="lead.follow_up_paused"
                  :disabled="isTogglingFollowUp"
                  class="text-xs px-3 py-1 rounded border border-n-weak text-n-slate-10 hover:text-woot-600 hover:border-woot-300 transition-colors disabled:opacity-50"
                  @click="onResumeFollowUps"
                >
                  Resume Follow-ups
                </button>
              </div>
            </section>

            <!-- Conversation link -->
            <section v-if="lead.conversation_id" class="px-5 py-4">
              <h3
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-2"
              >
                {{ t('NEXCEO.LEAD.DRAWER.CONVERSATION') }}
              </h3>
              <a
                :href="`/app/accounts/${lead.account_id}/conversations/${lead.conversation_id}`"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1.5 text-sm text-woot-500 hover:text-woot-600 hover:underline"
              >
                <span class="i-lucide-external-link size-4" />
                {{ t('NEXCEO.LEAD.ACTIONS.OPEN_CONVERSATION') }} #{{
                  lead.conversation_id
                }}
              </a>
            </section>

            <!-- Move stage -->
            <section class="rounded-xl border border-n-weak bg-n-background p-4">
              <h3 class="flex items-center gap-2 text-sm font-bold text-n-slate-12 mb-3">
                <span
                  class="flex items-center justify-center size-6 rounded-md bg-woot-50 dark:bg-woot-500/10 text-woot-600 dark:text-woot-400 shrink-0"
                >
                  <span class="i-lucide-arrow-right-left size-3.5" />
                </span>
                {{ t('NEXCEO.LEAD.ACTIONS.MOVE_STAGE') }}
              </h3>
              <div class="flex items-center gap-2">
                <select
                  v-model="selectedStage"
                  class="flex-1 text-sm !border !border-solid border-n-weak dark:border-n-strong rounded-lg px-3 py-1.5 bg-n-background dark:bg-n-solid-3 text-n-slate-12 outline-none focus:ring-2 focus:ring-woot-300"
                >
                  <option value="" disabled>
                    {{ t('NEXCEO.LEAD.FIELDS.STAGE') }}
                  </option>
                  <option
                    v-for="stage in otherStages"
                    :key="stage.id"
                    :value="stage.key"
                  >
                    {{ stage.name }}
                  </option>
                </select>
                <button
                  :disabled="!selectedStage || isMovingStage"
                  class="px-3 py-1.5 text-sm bg-woot-500 text-white rounded-lg disabled:opacity-50 hover:bg-woot-600 transition-colors"
                  @click="onMoveStage"
                >
                  {{ isMovingStage ? '…' : t('NEXCEO.KANBAN.CARD.MOVE_STAGE') }}
                </button>
              </div>
            </section>

            <!-- Assign agent -->
            <section class="rounded-xl border border-n-weak bg-n-background p-4">
              <h3 class="flex items-center gap-2 text-sm font-bold text-n-slate-12 mb-3">
                <span
                  class="flex items-center justify-center size-6 rounded-md bg-woot-50 dark:bg-woot-500/10 text-woot-600 dark:text-woot-400 shrink-0"
                >
                  <span class="i-lucide-user-round size-3.5" />
                </span>
                {{ t('NEXCEO.LEAD.ACTIONS.ASSIGN_AGENT') }}
              </h3>
              <div class="flex items-center gap-2">
                <select
                  v-model="selectedAgent"
                  class="flex-1 text-sm !border !border-solid border-n-weak dark:border-n-strong rounded-lg px-3 py-1.5 bg-n-background dark:bg-n-solid-3 text-n-slate-12 outline-none focus:ring-2 focus:ring-woot-300"
                >
                  <option value="">
                    {{ t('NEXCEO.KANBAN.FILTERS.ALL_AGENTS') }}
                  </option>
                  <option
                    v-for="agent in agents"
                    :key="agent.id"
                    :value="agent.id"
                  >
                    {{ agent.name }}
                  </option>
                </select>
                <button
                  :disabled="isAssigning"
                  class="px-3 py-1.5 text-sm bg-woot-500 text-white rounded-lg disabled:opacity-50 hover:bg-woot-600 transition-colors"
                  @click="onAssignAgent"
                >
                  {{
                    isAssigning ? '…' : t('NEXCEO.LEAD.ACTIONS.ASSIGN_AGENT')
                  }}
                </button>
              </div>
            </section>

            <!-- Schedule Visit / Meeting -->
            <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
            <section class="rounded-xl border border-n-weak bg-n-background p-4">
              <h3 class="flex items-center gap-2 text-sm font-bold text-n-slate-12 mb-3">
                <span
                  class="flex items-center justify-center size-6 rounded-md bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 shrink-0"
                >
                  <span class="i-lucide-calendar-clock size-3.5" />
                </span>
                Schedule Visit / Meeting
              </h3>
              <div class="flex flex-wrap gap-2">
                <button
                  class="flex items-center gap-1.5 text-xs text-green-700 !border !border-solid border-green-300 dark:border-green-700 rounded-lg px-3 py-1.5 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  @click="openVisitForm('site_visit')"
                >
                  <span class="i-lucide-map-pin size-3.5" />
                  Site Visit
                </button>
                <button
                  class="flex items-center gap-1.5 text-xs text-woot-600 !border !border-solid border-woot-300 dark:border-woot-700 rounded-lg px-3 py-1.5 hover:bg-woot-50 dark:hover:bg-woot-900/20 transition-colors font-medium"
                  @click="openVisitForm('video_call')"
                >
                  <span class="i-lucide-video size-3.5" />
                  Google Meet
                </button>
                <button
                  class="flex items-center gap-1.5 text-xs text-violet-600 !border !border-solid border-violet-300 dark:border-violet-700 rounded-lg px-3 py-1.5 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                  @click="openVisitForm('office_meeting')"
                >
                  <span class="i-lucide-building-2 size-3.5" />
                  Office Meeting
                </button>
              </div>

              <!-- Resend confirmation (shown when a visit is scheduled) -->
              <div
                v-if="lead?.next_site_visit_id"
                class="mt-3 pt-3 border-t border-n-weak/60"
              >
                <button
                  class="flex items-center gap-1.5 text-xs text-n-slate-9 hover:text-woot-600 transition-colors"
                  :disabled="isResendingConfirmation"
                  @click="resendConfirmation(lead.next_site_visit_id)"
                >
                  <span
                    :class="
                      isResendingConfirmation
                        ? 'i-lucide-loader-2 animate-spin'
                        : 'i-lucide-send size-3.5'
                    "
                  />
                  Resend Confirmation
                </button>
              </div>
            </section>
            <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

            <!-- Share Documents (M26.18, Goal 4) -->
            <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
            <section v-if="lead.project_id" class="rounded-xl border border-n-weak bg-n-background p-4">
              <h3 class="flex items-center gap-2 text-sm font-bold text-n-slate-12 mb-3">
                <span
                  class="flex items-center justify-center size-6 rounded-md bg-slate-100 dark:bg-n-solid-3 text-slate-600 dark:text-n-slate-11 shrink-0"
                >
                  <span class="i-lucide-file-text size-3.5" />
                </span>
                Documents
              </h3>
              <button
                class="flex items-center gap-1.5 text-xs text-n-slate-10 !border !border-solid border-n-weak rounded-lg px-3 py-1.5 hover:bg-n-alpha-1 transition-colors"
                @click="isShareDocumentsOpen = true"
              >
                <span class="i-lucide-file-text size-3.5" />
                Share Documents
              </button>
            </section>
            <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

            <!-- AI controls -->
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <section
              v-if="lead.conversation_id"
              class="rounded-xl border border-n-weak bg-n-background p-4 space-y-3"
            >
              <h3 class="flex items-center gap-2 text-sm font-bold text-n-slate-12">
                <span
                  class="flex items-center justify-center size-6 rounded-md bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 shrink-0"
                >
                  <span class="i-lucide-bot size-3.5" />
                </span>
                AI Employee
              </h3>

              <!-- AI Employee identity -->
              <div v-if="aiEmployee" class="flex items-center gap-2">
                <span
                  class="flex items-center justify-center size-8 rounded-full bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 text-xs font-bold shrink-0"
                >
                  {{ aiEmployee.name?.[0]?.toUpperCase() || 'A' }}
                </span>
                <div class="min-w-0">
                  <p class="text-sm font-medium text-n-slate-12 leading-tight">
                    {{ aiEmployee.name }}
                  </p>
                  <p
                    v-if="aiEmployee.role_title"
                    class="text-xs text-n-slate-7 leading-tight"
                  >
                    {{ aiEmployee.role_title }}
                  </p>
                </div>
              </div>

              <!-- Status badge -->
              <div class="flex items-center gap-2 flex-wrap">
                <span
                  class="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium border"
                  :class="aiStatusClass"
                >
                  <span class="size-3" :class="aiStatusIcon" />
                  {{ aiStatusLabel }}
                </span>
                <span
                  v-if="aiEmployee && aiEmployee.auto_reply_enabled === false"
                  class="text-xs text-n-slate-7"
                >
                  Auto-reply off
                </span>
                <span
                  v-if="aiState === 'waiting_for_customer'"
                  class="text-xs text-n-slate-7"
                >
                  Waiting for next customer message
                </span>
              </div>

              <!-- Disabled reason explanation -->
              <div
                v-if="aiDisabledByConfig"
                class="flex items-start gap-2 px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-xs text-n-slate-9"
              >
                <span class="i-lucide-info size-3.5 shrink-0 mt-0.5" />
                <span>
                  <template v-if="aiDisabledReason === 'env_disabled'">
                    AI is disabled globally. Set
                    <code
                      class="font-mono text-n-slate-12 bg-n-alpha-2 px-1 rounded"
                      >NEXCEO_AI_ENABLED=true</code
                    >
                    in <code class="font-mono text-n-slate-12">.env</code>
                    and restart Chatwoot to enable AI replies.
                  </template>
                  <template v-else-if="aiDisabledReason === 'openai_missing'">
                    OpenAI key missing. Configure it in
                    <router-link
                      :to="`/app/accounts/${lead.account_id}/settings/integrations/openai`"
                      class="text-woot-500 underline hover:text-woot-600"
                    >
                      Settings → Integrations → OpenAI </router-link
                    >.
                  </template>
                  <template v-else-if="aiDisabledReason === 'project_disabled'">
                    AI is disabled for this project. Enable it in Project
                    Details.
                  </template>
                  <template
                    v-else-if="aiDisabledReason === 'employee_inactive'"
                  >
                    AI employee is paused or disabled. Change status in AI
                    Employee Settings.
                  </template>
                  <template v-else-if="aiDisabledReason === 'auto_reply_off'">
                    Auto-reply is turned off. Enable it in AI Employee Settings.
                  </template>
                </span>
              </div>

              <!-- AI Explainability: last intent + missing fields -->
              <!-- eslint-disable vue/no-bare-strings-in-template -->
              <div
                v-if="
                  lead.ai_state?.last_intent ||
                  lead.ai_state?.missing_fields?.length
                "
                class="space-y-1.5"
              >
                <div
                  v-if="lead.ai_state?.last_intent"
                  class="flex items-start gap-1.5 text-xs px-3 py-2 rounded-lg bg-woot-50 dark:bg-woot-800/20 border border-woot-100 dark:border-woot-700/40"
                >
                  <span
                    class="i-lucide-brain-circuit size-3.5 text-woot-500 shrink-0 mt-0.5"
                  />
                  <div>
                    <span class="font-medium text-woot-700 dark:text-woot-400"
                      >Last intent:
                    </span>
                    <span class="text-n-slate-11 capitalize">{{
                      lead.ai_state.last_intent.replace(/_/g, ' ')
                    }}</span>
                  </div>
                </div>
                <div
                  v-if="lead.ai_state?.missing_fields?.length"
                  class="flex items-start gap-1.5 text-xs px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-700/40"
                >
                  <span
                    class="i-lucide-list-todo size-3.5 text-amber-500 shrink-0 mt-0.5"
                  />
                  <div>
                    <span class="font-medium text-amber-700 dark:text-amber-400"
                      >Still collecting:
                    </span>
                    <span class="text-amber-800 dark:text-amber-300">{{
                      lead.ai_state.missing_fields.join(', ')
                    }}</span>
                  </div>
                </div>
              </div>
              <!-- eslint-enable vue/no-bare-strings-in-template -->

              <!-- Action buttons -->
              <div
                v-if="!aiDisabledByConfig"
                class="flex items-center gap-2 flex-wrap"
              >
                <button
                  v-if="!aiPaused"
                  :disabled="isTogglingAi"
                  class="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-800/10 transition disabled:opacity-50"
                  @click="onPauseAi"
                >
                  <span class="i-lucide-pause-circle size-3.5" />
                  {{ isTogglingAi ? '…' : 'Pause AI' }}
                </button>
                <button
                  v-if="aiPaused"
                  :disabled="isTogglingAi"
                  class="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-success-300 text-success-700 hover:bg-success-50 dark:hover:bg-success-800/10 transition disabled:opacity-50"
                  @click="onResumeAi"
                >
                  <span class="i-lucide-play-circle size-3.5" />
                  {{ isTogglingAi ? '…' : 'Resume AI' }}
                </button>
                <button
                  v-if="!aiPaused"
                  :disabled="isTogglingAi"
                  class="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition disabled:opacity-50"
                  @click="onPauseAi"
                >
                  <span class="i-lucide-user-check size-3.5" />
                  {{ 'Human Takeover' }}
                </button>
              </div>
            </section>

            <!-- eslint-enable vue/no-bare-strings-in-template -->

            <!-- Danger actions -->
            <section class="rounded-xl border border-n-weak bg-n-background p-4 space-y-3">
              <h3 class="flex items-center gap-2 text-sm font-bold text-n-slate-12 mb-1">
                <span
                  class="flex items-center justify-center size-6 rounded-md bg-woot-50 dark:bg-woot-500/10 text-woot-600 dark:text-woot-400 shrink-0"
                >
                  <span class="i-lucide-zap size-3.5" />
                </span>
                Quick Actions
              </h3>

              <!-- Mark lost -->
              <div v-if="!showLostConfirm">
                <button
                  class="w-full flex items-center gap-1.5 text-sm text-red-600 bg-red-50 dark:bg-red-500/10 !border !border-solid border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-100 dark:hover:bg-red-500/20 transition"
                  @click="showLostConfirm = true"
                >
                  <span class="i-lucide-x-circle size-3.5" />
                  {{ t('NEXCEO.LEAD.ACTIONS.MARK_LOST') }}
                </button>
              </div>
              <div
                v-else
                class="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2"
              >
                <p class="text-sm text-red-700">
                  {{ t('NEXCEO.LEAD.MARK_LOST_CONFIRM') }}
                </p>
                <input
                  v-model="lostReason"
                  type="text"
                  :placeholder="t('NEXCEO.LEAD.MARK_LOST_REASON')"
                  class="w-full text-sm border border-red-200 rounded px-2 py-1 outline-none"
                />
                <div class="flex gap-2">
                  <button
                    class="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                    @click="onMarkLost"
                  >
                    {{ t('NEXCEO.LEAD.ACTIONS.MARK_LOST') }}
                  </button>
                  <button
                    class="text-sm text-n-slate-9 px-3 py-1 hover:text-n-slate-12"
                    @click="showLostConfirm = false"
                  >
                    {{ t('NEXCEO.LEAD.FORM.CANCEL') }}
                  </button>
                </div>
              </div>

              <!-- Mark booked -->
              <div v-if="!showBookedConfirm">
                <button
                  class="w-full flex items-center gap-1.5 text-sm text-green-700 bg-green-50 dark:bg-green-500/10 !border !border-solid border-green-200 rounded-lg px-3 py-1.5 hover:bg-green-100 dark:hover:bg-green-500/20 transition"
                  @click="showBookedConfirm = true"
                >
                  <span class="i-lucide-check-circle-2 size-3.5" />
                  {{ t('NEXCEO.LEAD.ACTIONS.MARK_BOOKED') }}
                </button>
              </div>
              <div
                v-else
                class="bg-green-50 border border-green-300 rounded-lg p-3 space-y-2"
              >
                <p class="text-sm text-green-700">
                  {{ t('NEXCEO.LEAD.MARK_BOOKED_CONFIRM') }}
                </p>
                <div class="flex gap-2">
                  <button
                    class="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                    @click="onMarkBooked"
                  >
                    {{ t('NEXCEO.LEAD.ACTIONS.MARK_BOOKED') }}
                  </button>
                  <button
                    class="text-sm text-n-slate-9 px-3 py-1 hover:text-n-slate-12"
                    @click="showBookedConfirm = false"
                  >
                    {{ t('NEXCEO.LEAD.FORM.CANCEL') }}
                  </button>
                </div>
              </div>

              <!-- Verify / Reject Lead -->
              <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
              <div class="border-t border-n-weak pt-3 mt-3">
                <p
                  class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-2"
                >
                  Verification
                </p>

                <!-- Rejected / Junk status banner -->
                <div
                  v-if="isRejectedOrJunk"
                  class="mb-3 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 px-3 py-2"
                >
                  <span
                    class="i-lucide-ban size-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5"
                  />
                  <div class="min-w-0 flex-1">
                    <p
                      class="text-sm font-semibold text-red-700 dark:text-red-400"
                    >
                      {{ lead?.status_label || 'Rejected' }}
                    </p>
                    <p
                      v-if="lead?.rejection_reason"
                      class="text-xs text-red-600/80 dark:text-red-400/70 mt-0.5"
                    >
                      {{ lead.rejection_reason }}
                    </p>
                    <p
                      v-if="lead?.rejected_at"
                      class="text-xs text-red-500/70 mt-0.5"
                    >
                      {{ formatDate(lead.rejected_at) }}
                    </p>
                  </div>
                </div>

                <div class="flex items-center gap-2 flex-wrap">
                  <span
                    class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
                    :class="{
                      'bg-woot-100 text-woot-700':
                        lead?.verification_status === 'ai_verified',
                      'bg-green-100 text-green-700':
                        lead?.verification_status === 'human_verified',
                      'bg-red-100 text-red-600':
                        lead?.verification_status === 'rejected' ||
                        lead?.lead_status === 'junk',
                      'bg-slate-100 text-slate-500':
                        !lead?.verification_status ||
                        lead?.verification_status === 'unverified',
                    }"
                  >
                    {{
                      lead?.lead_status === 'junk'
                        ? 'Junk'
                        : lead?.verification_status === 'ai_verified'
                          ? 'AI Verified'
                          : lead?.verification_status === 'human_verified'
                            ? 'Human Verified'
                            : lead?.verification_status === 'rejected'
                              ? 'Rejected'
                              : 'Unverified'
                    }}
                  </span>

                  <!-- Restore lead button (shown when rejected or junk) -->
                  <button
                    v-if="isRejectedOrJunk"
                    :disabled="isRestoring"
                    class="text-xs text-green-700 !border !border-solid border-green-300 rounded-lg px-3 py-1.5 hover:bg-green-50 transition disabled:opacity-50 flex items-center gap-1"
                    @click="onRestoreLead"
                  >
                    <span
                      v-if="isRestoring"
                      class="i-lucide-loader-2 size-3 animate-spin"
                    />
                    <span v-else class="i-lucide-undo-2 size-3" />
                    Restore Lead
                  </button>

                  <button
                    v-if="
                      !isRejectedOrJunk &&
                      lead?.verification_status !== 'human_verified'
                    "
                    :disabled="isVerifying"
                    class="text-xs text-green-700 !border !border-solid border-green-300 rounded-lg px-3 py-1.5 hover:bg-green-50 transition disabled:opacity-50"
                    @click="onVerify"
                  >
                    <span
                      v-if="isVerifying"
                      class="i-lucide-loader-2 size-3 animate-spin inline-block mr-1"
                    />
                    Mark as Verified
                  </button>

                  <!-- Reject button — opens confirmation dialog -->
                  <button
                    v-if="!isRejectedOrJunk"
                    class="text-xs text-red-600 !border !border-solid border-red-300 rounded-lg px-3 py-1.5 hover:bg-red-50 transition"
                    @click="showRejectConfirm = true"
                  >
                    Reject / Not a Lead
                  </button>

                  <!-- Send Team Alert -->
                  <button
                    :disabled="isSendingAlert"
                    class="w-full text-xs text-woot-700 bg-woot-50 dark:bg-woot-500/10 !border !border-solid border-woot-300 rounded-lg px-3 py-1.5 hover:bg-woot-100 dark:hover:bg-woot-500/20 transition disabled:opacity-50 flex items-center gap-1.5"
                    @click="onSendTeamAlert()"
                  >
                    <span
                      v-if="isSendingAlert"
                      class="i-lucide-loader-2 size-3 animate-spin"
                    />
                    <span v-else class="i-lucide-bell-ring size-3" />
                    Send Team Alert
                  </button>
                </div>

                <!-- Automation indicators: welcome / alert sent status -->
                <div
                  v-if="
                    lead?.metadata?.customer_welcome_sent_at ||
                    lead?.metadata?.manual_verified_alert_sent_at ||
                    lead?.metadata?.ai_verified_alert_sent_at
                  "
                  class="mt-2 flex items-center gap-2 flex-wrap"
                >
                  <span
                    v-if="lead?.metadata?.customer_welcome_sent_at"
                    class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-700/40"
                    :title="`Welcome sent: ${formatDate(lead.metadata.customer_welcome_sent_at)}`"
                  >
                    <span class="i-lucide-message-square-check size-3" />
                    Welcome sent
                  </span>
                  <span
                    v-if="
                      lead?.metadata?.manual_verified_alert_sent_at ||
                      lead?.metadata?.ai_verified_alert_sent_at
                    "
                    class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-woot-50 text-woot-700 border border-woot-200 dark:bg-woot-900/20 dark:text-woot-400 dark:border-woot-700/40"
                    :title="`Team alerted: ${formatDate(lead.metadata.manual_verified_alert_sent_at || lead.metadata.ai_verified_alert_sent_at)}`"
                  >
                    <span class="i-lucide-bell-ring size-3" />
                    Team alerted
                  </span>
                </div>

                <!-- Reject confirmation dialog -->
                <div
                  v-if="showRejectConfirm"
                  class="mt-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/20 space-y-2"
                >
                  <p
                    class="text-sm font-semibold text-red-700 dark:text-red-400"
                  >
                    Reject this lead?
                  </p>
                  <p class="text-xs text-n-slate-7">
                    This will stop AI follow-ups and exclude the lead from
                    Verified Leads.
                  </p>
                  <select
                    v-model="rejectLeadReason"
                    class="w-full text-xs border border-n-weak dark:border-n-strong rounded-lg px-2 py-1.5 bg-white dark:bg-n-solid-3 text-n-slate-12"
                  >
                    <option value="">Select a reason (optional)</option>
                    <option v-for="r in REJECT_REASONS" :key="r" :value="r">
                      {{ r }}
                    </option>
                  </select>
                  <div class="flex gap-2 pt-1">
                    <button
                      :disabled="isRejecting"
                      class="flex-1 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg px-3 py-1.5 transition disabled:opacity-50 flex items-center justify-center gap-1"
                      @click="onConfirmRejectLead"
                    >
                      <span
                        v-if="isRejecting"
                        class="i-lucide-loader-2 size-3 animate-spin"
                      />
                      Confirm Reject
                    </button>
                    <button
                      class="flex-1 text-xs border border-n-weak rounded-lg px-3 py-1.5 text-n-slate-9 hover:bg-n-alpha-2 transition"
                      @click="
                        showRejectConfirm = false;
                        rejectLeadReason = '';
                      "
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
            <!-- Pending AI Actions -->
            <section
              v-if="pendingActions && pendingActions.length"
              class="px-5 py-4"
            >
              <div class="flex items-center gap-2 mb-3">
                <h3
                  class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest"
                >
                  Pending AI Actions
                </h3>
                <span
                  v-if="pendingCount > 0"
                  class="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-500 text-white tabular-nums"
                >
                  {{ pendingCount }}
                </span>
              </div>

              <p
                v-if="isLoadingPendingActions"
                class="text-xs text-n-slate-8 py-2"
              >
                Loading…
              </p>

              <!-- Reject inline form -->
              <div
                v-if="rejectingActionId"
                class="mb-3 p-3 rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 space-y-2"
              >
                <p class="text-xs font-medium text-red-700 dark:text-red-400">
                  Reject this action?
                </p>
                <textarea
                  v-model="rejectReason"
                  rows="2"
                  placeholder="Reason (optional)"
                  class="w-full text-xs px-2.5 py-1.5 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500 resize-none"
                />
                <div class="flex gap-2">
                  <button
                    class="flex-1 py-1 text-xs rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                    @click="submitRejectAction"
                  >
                    Confirm
                  </button>
                  <button
                    class="flex-1 py-1 text-xs rounded-lg border border-n-weak text-n-slate-8 transition-colors"
                    @click="rejectingActionId = null"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              <div class="space-y-2">
                <div
                  v-for="action in pendingActions"
                  :key="action.id"
                  class="rounded-xl border border-n-weak bg-n-background p-3 space-y-2"
                >
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex items-center gap-1.5 min-w-0">
                      <span
                        class="i-lucide-shield-alert size-3.5 text-amber-500 shrink-0"
                      />
                      <span
                        class="text-xs font-semibold text-n-slate-11 truncate capitalize"
                      >
                        {{ pendingActionLabel(action.action_type) }}
                      </span>
                    </div>
                    <span
                      class="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 capitalize"
                      :class="{
                        'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400':
                          action.status === 'pending',
                        'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400':
                          action.status === 'executed' ||
                          action.status === 'approved',
                        'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400':
                          action.status === 'rejected' ||
                          action.status === 'failed',
                        'bg-n-alpha-1 text-n-slate-7':
                          action.status === 'expired',
                      }"
                    >
                      {{ action.status }}
                    </span>
                  </div>

                  <p v-if="action.reason" class="text-xs text-n-slate-8">
                    {{ action.reason }}
                  </p>

                  <!-- Document preview -->
                  <div
                    v-if="action.document"
                    class="flex items-center gap-2 text-xs text-n-slate-9 bg-n-alpha-1 rounded-lg px-2 py-1.5"
                  >
                    <span
                      class="i-lucide-file-text size-3 text-woot-500 shrink-0"
                    />
                    <span class="truncate">
                      {{
                        action.document.document_title ||
                        action.document.document_type
                      }}
                    </span>
                    <a
                      v-if="action.document.document_url"
                      :href="action.document.document_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="ml-auto text-woot-500 hover:underline shrink-0"
                    >
                      Preview
                    </a>
                  </div>

                  <div
                    v-if="action.status === 'pending'"
                    class="flex gap-2 pt-0.5"
                  >
                    <button
                      class="flex-1 py-1 text-xs rounded-lg bg-woot-500 text-white hover:bg-woot-600 transition-colors"
                      @click="approvePendingAction(action)"
                    >
                      Approve
                    </button>
                    <button
                      class="flex-1 py-1 text-xs rounded-lg border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                      @click="startRejectAction(action)"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <!-- AI Logs -->
            <section class="px-5 py-4">
              <h3
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                AI Activity Log
              </h3>
              <p v-if="isLoadingAiLogs" class="text-xs text-n-slate-8 py-2">
                Loading…
              </p>
              <p
                v-else-if="aiLogs.length === 0"
                class="text-xs text-n-slate-8 py-2"
              >
                No AI activity yet.
              </p>
              <ol v-else class="space-y-2">
                <li
                  v-for="log in aiLogs.slice(0, 30)"
                  :key="log.id"
                  class="rounded-lg border border-n-weak overflow-hidden"
                >
                  <!-- Summary row -->
                  <button
                    class="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-n-alpha-1 transition-colors"
                    @click="
                      showAiLogDetail =
                        showAiLogDetail === log.id ? null : log.id
                    "
                  >
                    <span
                      class="size-3.5 shrink-0 mt-0.5 text-n-slate-8"
                      :class="aiLogEventIcon(log.event_type)"
                    />
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <span
                          class="text-xs font-medium text-n-slate-11 capitalize"
                        >
                          {{ log.event_type.replace(/_/g, ' ') }}
                        </span>
                        <span
                          class="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                          :class="aiLogStatusClass(log.status)"
                        >
                          {{ log.status }}
                        </span>
                        <span
                          v-if="log.provider"
                          class="text-[10px] text-n-slate-8"
                        >
                          {{ log.provider }}/{{ log.model }}
                        </span>
                        <span
                          v-if="log.duration_ms"
                          class="text-[10px] text-n-slate-7"
                        >
                          {{ log.duration_ms }}ms
                        </span>
                      </div>
                      <p
                        v-if="log.input_summary || log.output_summary"
                        class="text-[11px] text-n-slate-9 mt-0.5 truncate"
                      >
                        {{ log.input_summary || log.output_summary }}
                      </p>
                      <p class="text-[10px] text-n-slate-7 mt-0.5">
                        {{ formatDate(log.created_at) }}
                      </p>
                    </div>
                    <span
                      class="size-3 shrink-0 text-n-slate-7 mt-0.5"
                      :class="
                        showAiLogDetail === log.id
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                    />
                  </button>

                  <!-- Detail panel (admin debug) -->
                  <div
                    v-if="showAiLogDetail === log.id"
                    class="border-t border-n-weak px-3 py-2 bg-n-alpha-1 space-y-1.5 text-[11px] text-n-slate-9"
                  >
                    <div v-if="log.error_message" class="text-red-600">
                      Error: {{ log.error_message }}
                    </div>
                    <div v-if="log.tokens_total">
                      Tokens: {{ log.tokens_input }} in /
                      {{ log.tokens_output }} out ({{ log.tokens_total }} total)
                    </div>
                    <div v-if="log.cost_estimate">
                      Cost: ~${{ Number(log.cost_estimate).toFixed(6) }}
                    </div>
                    <div v-if="log.reply">
                      <span class="font-medium">Reply preview:</span>
                      {{ log.reply }}
                    </div>
                    <details v-if="log.actions_json?.length">
                      <summary class="cursor-pointer font-medium">
                        Actions ({{ log.actions_json.length }})
                      </summary>
                      <pre
                        class="mt-1 text-[10px] whitespace-pre-wrap break-all"
                        >{{ JSON.stringify(log.actions_json, null, 2) }}</pre
                      >
                    </details>
                  </div>
                </li>
              </ol>
            </section>

            <!-- Activity timeline -->
            <section class="px-5 py-4">
              <h3
                class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
              >
                {{ t('NEXCEO.LEAD.DRAWER.ACTIVITIES') }}
              </h3>

              <!-- Ownership journey strip -->
              <div
                v-if="ownershipChain.length > 1"
                class="flex flex-col gap-1.5 mb-4 p-3 rounded-lg bg-n-alpha-1 border border-n-weak"
              >
                <p
                  class="text-[11px] font-semibold text-n-slate-7 uppercase tracking-wider mb-0.5"
                >
                  Ownership Journey
                </p>
                <div
                  v-for="(node, idx) in ownershipChain"
                  :key="idx"
                  class="flex items-center gap-2"
                >
                  <span
                    class="text-xs font-medium px-2 py-0.5 rounded-full"
                    :class="
                      idx === ownershipChain.length - 1
                        ? 'bg-woot-50 text-woot-700 dark:bg-woot-500/10 dark:text-woot-400'
                        : 'bg-n-alpha-2 text-n-slate-9'
                    "
                  >
                    {{ node.label }}
                  </span>
                  <span
                    v-if="idx < ownershipChain.length - 1"
                    class="i-lucide-arrow-down size-3 text-n-slate-6"
                  />
                </div>
              </div>

              <div
                v-if="isLoadingActivities"
                class="text-xs text-n-slate-8 py-2"
              >
                {{ t('NEXCEO.LEAD.DRAWER.LOADING_ACTIVITIES') }}
              </div>
              <div
                v-else-if="activities.length === 0"
                class="text-xs text-n-slate-8 py-2"
              >
                {{ t('NEXCEO.LEAD.DRAWER.NO_ACTIVITY') }}
              </div>
              <ol v-else class="space-y-3">
                <li
                  v-for="activity in activities"
                  :key="activity.id"
                  class="flex gap-3"
                >
                  <span
                    :class="[
                      activityIcon(activity.activity_type),
                      activity.activity_type === 'workforce_event' ||
                      activity.activity_type === 'human_handoff'
                        ? 'text-woot-500'
                        : 'text-n-slate-8',
                    ]"
                    class="size-4 shrink-0 mt-0.5"
                  />
                  <div class="min-w-0">
                    <p
                      class="text-sm flex items-center gap-1.5 flex-wrap"
                      :class="
                        activity.activity_type === 'workforce_event' ||
                        activity.activity_type === 'human_handoff'
                          ? 'text-woot-700 dark:text-woot-400 font-medium'
                          : 'text-n-slate-11'
                      "
                    >
                      <span
                        class="i-lucide-check size-3.5 shrink-0 text-green-600 dark:text-green-400"
                      />
                      {{ activity.friendly_title || activity.title }}
                      <span
                        v-if="activity.actor_category"
                        class="inline-flex items-center gap-0.5 text-[11px] font-normal"
                        :class="actorStyle(activity.actor_category).class"
                      >
                        <span
                          :class="actorStyle(activity.actor_category).icon"
                          class="size-3"
                        />
                        {{ activity.actor_name }}
                      </span>
                    </p>
                    <p
                      v-if="activity.description"
                      class="text-xs text-n-slate-9"
                    >
                      {{ activity.description }}
                    </p>
                    <p class="text-xs text-n-slate-7 mt-0.5">
                      {{ formatDate(activity.created_at) }}
                    </p>
                  </div>
                </li>
              </ol>
            </section>
          </div>

          <!-- AI Intelligence tab (read-only; Nexceo::Ai::RuntimeInspector) -->
          <div
            v-else
            class="flex-1 overflow-y-auto divide-y divide-n-weak dark:divide-n-strong"
          >
            <div v-if="runtimeLoading" class="flex justify-center py-10">
              <span
                class="i-lucide-loader-2 size-5 animate-spin text-n-slate-7"
              />
            </div>

            <p
              v-else-if="runtimeError"
              class="text-xs text-n-slate-8 px-5 py-6 text-center"
            >
              {{ runtimeError }}
            </p>

            <template v-else-if="runtimeSnapshot">
              <!-- Ownership & capability -->
              <section class="px-5 py-4">
                <h3
                  class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
                >
                  AI Ownership
                </h3>
                <div class="flex items-center gap-2 flex-wrap mb-2">
                  <span
                    class="text-xs font-medium px-2 py-1 rounded-full bg-woot-50 text-woot-700 dark:bg-woot-500/10 dark:text-woot-400"
                  >
                    {{ runtimeSnapshot.current_owner }}
                  </span>
                  <span
                    v-if="runtimeSnapshot.current_capability"
                    class="text-xs font-medium px-2 py-1 rounded-full bg-n-alpha-2 text-n-slate-9"
                  >
                    <span class="i-lucide-cpu size-3 inline-block mr-0.5" />
                    {{ formatKey(runtimeSnapshot.current_capability) }}
                  </span>
                </div>
                <p
                  v-if="runtimeSnapshot.ai_owner_switched_at"
                  class="text-xs text-n-slate-7"
                >
                  Since {{ formatDate(runtimeSnapshot.ai_owner_switched_at) }}
                </p>
                <div
                  v-if="(runtimeSnapshot.capabilities_used || []).length"
                  class="flex flex-wrap gap-1.5 mt-2"
                >
                  <span
                    v-for="cap in runtimeSnapshot.capabilities_used"
                    :key="cap"
                    class="text-[11px] px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-8"
                  >
                    {{ formatKey(cap) }}
                  </span>
                </div>
              </section>

              <!-- Conversation state -->
              <section class="px-5 py-4">
                <h3
                  class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
                >
                  Conversation State
                </h3>
                <div class="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p class="text-n-slate-7">Current state</p>
                    <p class="text-n-slate-11 font-medium">
                      {{ formatKey(runtimeSnapshot.conversation_state || '—') }}
                    </p>
                  </div>
                  <div>
                    <p class="text-n-slate-7">Previous state</p>
                    <p class="text-n-slate-11 font-medium">
                      {{ formatKey(runtimeSnapshot.previous_state || '—') }}
                    </p>
                  </div>
                  <div>
                    <p class="text-n-slate-7">Relationship stage</p>
                    <p class="text-n-slate-11 font-medium">
                      {{ formatKey(runtimeSnapshot.relationship_stage || '—') }}
                    </p>
                  </div>
                  <div>
                    <p class="text-n-slate-7">Decision stage</p>
                    <p class="text-n-slate-11 font-medium">
                      {{ formatKey(runtimeSnapshot.decision_stage || '—') }}
                    </p>
                  </div>
                </div>
              </section>

              <!-- Sales intelligence -->
              <section class="px-5 py-4">
                <h3
                  class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
                >
                  Sales Intelligence
                </h3>
                <div class="grid grid-cols-2 gap-2 text-xs mb-2">
                  <div>
                    <p class="text-n-slate-7">Sales intent</p>
                    <p class="text-n-slate-11 font-medium">
                      {{
                        formatKey(runtimeSnapshot.sales_intent?.intent) || '—'
                      }}
                      <span
                        v-if="runtimeSnapshot.sales_intent?.confidence"
                        class="text-n-slate-7 font-normal"
                      >
                        ({{
                          Math.round(
                            runtimeSnapshot.sales_intent.confidence * 100
                          )
                        }}%)
                      </span>
                    </p>
                  </div>
                  <div>
                    <p class="text-n-slate-7">Strategy</p>
                    <p class="text-n-slate-11 font-medium">
                      {{ formatKey(runtimeSnapshot.sales_strategy) || '—' }}
                    </p>
                  </div>
                  <div>
                    <p class="text-n-slate-7">Conversation goal</p>
                    <p class="text-n-slate-11 font-medium">
                      {{ formatKey(runtimeSnapshot.conversation_goal) || '—' }}
                    </p>
                  </div>
                  <div>
                    <p class="text-n-slate-7">Playbook</p>
                    <p class="text-n-slate-11 font-medium">
                      {{ formatKey(runtimeSnapshot.current_playbook) || '—' }}
                    </p>
                  </div>
                </div>
                <p
                  v-if="runtimeSnapshot.strategy_guidance"
                  class="text-xs text-n-slate-9 bg-n-alpha-1 rounded-lg px-3 py-2"
                >
                  {{ runtimeSnapshot.strategy_guidance }}
                </p>
                <p
                  v-if="runtimeSnapshot.objection_type"
                  class="text-xs text-amber-700 dark:text-amber-400 mt-2"
                >
                  <span
                    class="i-lucide-alert-triangle size-3 inline-block mr-0.5"
                  />
                  Objection: {{ formatKey(runtimeSnapshot.objection_type) }}
                </p>
              </section>

              <!-- Buying / curiosity scores -->
              <section class="px-5 py-4">
                <h3
                  class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
                >
                  Buying Signals
                </h3>
                <div class="grid grid-cols-2 gap-2 text-xs mb-2">
                  <div
                    v-if="
                      runtimeSnapshot.curiosity_score !== undefined &&
                      runtimeSnapshot.curiosity_score !== null
                    "
                  >
                    <p class="text-n-slate-7">Curiosity score</p>
                    <p class="text-n-slate-11 font-medium">
                      {{ runtimeSnapshot.curiosity_score }}
                    </p>
                  </div>
                  <div v-if="runtimeSnapshot.emotion">
                    <p class="text-n-slate-7">Emotion</p>
                    <p class="text-n-slate-11 font-medium capitalize">
                      {{ runtimeSnapshot.emotion }}
                    </p>
                  </div>
                </div>
                <div
                  v-if="objectEntries(runtimeSnapshot.buying_score).length"
                  class="flex flex-wrap gap-1.5"
                >
                  <span
                    v-for="[k, v] in objectEntries(
                      runtimeSnapshot.buying_score
                    )"
                    :key="k"
                    class="text-[11px] px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-8"
                  >
                    {{ formatKey(k) }}: {{ v }}
                  </span>
                </div>
              </section>

              <!-- Knowledge retrieved -->
              <section
                v-if="(runtimeSnapshot.knowledge_retrieved || []).length"
                class="px-5 py-4"
              >
                <h3
                  class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
                >
                  Knowledge Retrieved
                </h3>
                <div class="flex flex-wrap gap-1.5">
                  <span
                    v-for="cat in runtimeSnapshot.knowledge_retrieved"
                    :key="cat"
                    class="text-xs px-2 py-1 rounded-full bg-n-alpha-2 text-n-slate-9"
                  >
                    {{ formatKey(cat) }}
                  </span>
                </div>
              </section>

              <!-- Workflow & goal -->
              <section class="px-5 py-4">
                <h3
                  class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
                >
                  Goal &amp; Next Step
                </h3>
                <p class="text-xs text-n-slate-11 mb-1">
                  <span class="text-n-slate-7">Current goal:</span>
                  {{ runtimeSnapshot.current_goal }}
                </p>
                <p class="text-xs text-n-slate-11 mb-2">
                  <span class="text-n-slate-7">Expecting next:</span>
                  {{ runtimeSnapshot.next_expected_action }}
                </p>
                <div
                  v-if="(runtimeSnapshot.uncollected_fields || []).length"
                  class="flex flex-wrap gap-1.5"
                >
                  <span
                    v-for="f in runtimeSnapshot.uncollected_fields"
                    :key="f"
                    class="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400"
                  >
                    Missing: {{ formatKey(f) }}
                  </span>
                </div>
              </section>

              <!-- Pending tasks -->
              <section
                v-if="(runtimeSnapshot.pending_tasks || []).length"
                class="px-5 py-4"
              >
                <h3
                  class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
                >
                  Pending Tasks
                </h3>
                <ul class="flex flex-col gap-1.5">
                  <li
                    v-for="(task, idx) in runtimeSnapshot.pending_tasks"
                    :key="idx"
                    class="flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg bg-n-alpha-1"
                  >
                    <span class="text-n-slate-11">{{
                      formatKey(task.task_type)
                    }}</span>
                    <span class="text-n-slate-7">{{ task.status }}</span>
                  </li>
                </ul>
              </section>

              <!-- Recent workforce events -->
              <section
                v-if="(runtimeSnapshot.internal_events || []).length"
                class="px-5 py-4"
              >
                <h3
                  class="text-xs font-semibold text-n-slate-8 uppercase tracking-widest mb-3"
                >
                  Recent Workforce Events
                </h3>
                <ol class="flex flex-col gap-2">
                  <li
                    v-for="(event, idx) in runtimeSnapshot.internal_events"
                    :key="idx"
                    class="text-xs"
                  >
                    <p class="text-n-slate-11">{{ event.title }}</p>
                    <p class="text-n-slate-7">{{ formatDate(event.at) }}</p>
                  </li>
                </ol>
              </section>

              <p class="px-5 py-3 text-[11px] text-n-slate-6">
                Generated {{ formatDate(runtimeSnapshot.generated_at) }}
              </p>
            </template>
          </div>
        </div>
      </div>
    </transition>

    <!-- Site visit scheduling form (teleported outside drawer so z-index is independent) -->
    <SiteVisitForm
      :is-open="isVisitFormOpen"
      :prefill-lead="lead"
      :prefill-visit-type="visitFormPrefillType"
      @close="isVisitFormOpen = false"
      @saved="
        () => {
          isVisitFormOpen = false;
          $emit('updated');
        }
      "
    />

    <!-- Manual document sharing (M26.18, Goal 4) — reuses the same
         DocumentShareService the AI uses; teleported for the same
         independent-z-index reason as SiteVisitForm above. -->
    <ShareDocumentsModal
      :is-open="isShareDocumentsOpen"
      :lead="lead"
      @close="isShareDocumentsOpen = false"
      @shared="$emit('updated')"
    />
  </Teleport>
</template>

<style scoped>
.drawer-enter-active {
  transition: opacity 0.2s ease;
}
.drawer-leave-active {
  transition: opacity 0.15s ease;
}
.drawer-enter-active .relative {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
.drawer-leave-active .relative {
  transition: transform 0.2s ease;
}
.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}
.drawer-enter-from .relative {
  transform: translateX(100%);
}
.drawer-leave-to .relative {
  transform: translateX(100%);
}
</style>
